// Despre noi - the classic page is static; the V2 redesign (despre-noi-v2)
// adds its own interaction layer: scroll-triggered stat counters, pointer
// spotlight on cards + hero, scroll parallax on the hero watermark, a
// drag-to-scroll journey timeline and the fixed chapter progress rail.
// Every hook is a safe no-op on the classic page (elements absent).
import { qs, qsa } from '../lib/dom.js';
import { initTiltMarquee } from '../components/tilt-marquee.js';

const REDUCED = window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const FINE_POINTER = window.matchMedia &&
  window.matchMedia('(pointer: fine)').matches;

// Stat counters (the [data-counters] band) are handled globally by
// main.js → initStatCounters, so the About page doesn't observe them here.

// Pointer spotlight: track the cursor as --sx/--sy custom props so the CSS
// radial-gradient overlays (hero + [data-spot] cards) follow it.
function initSpotlight() {
  if (!FINE_POINTER || REDUCED) return;
  const hero = qs('[data-av2-hero]');
  if (hero) {
    hero.addEventListener('pointermove', (e) => {
      const r = hero.getBoundingClientRect();
      hero.style.setProperty('--sx', `${e.clientX - r.left}px`);
      hero.style.setProperty('--sy', `${e.clientY - r.top}px`);
      hero.classList.add('is-live');
    });
    hero.addEventListener('pointerleave', () => hero.classList.remove('is-live'));
  }
  qsa('[data-spot]').forEach((el) => {
    el.addEventListener('pointermove', (e) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--sx', `${e.clientX - r.left}px`);
      el.style.setProperty('--sy', `${e.clientY - r.top}px`);
    });
  });
}

// Scroll parallax on the hero's giant outlined watermark.
function initParallax() {
  const mark = qs('[data-av2-mark]');
  if (!mark || REDUCED) return;
  let ticking = false;
  const update = () => {
    ticking = false;
    const y = window.scrollY;
    if (y < window.innerHeight * 1.5) {
      mark.style.transform = `translateY(${y * -0.12}px)`;
    }
  };
  window.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }, { passive: true });
}

// Drag-to-scroll for the journey timeline (mouse users - touch scrolls
// natively). Click-through is suppressed only after a real drag.
function initTimelineDrag() {
  const rail = qs('[data-av2-timeline]');
  if (!rail || !FINE_POINTER) return;
  let startX = 0;
  let startLeft = 0;
  let dragged = false;
  let down = false;
  rail.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse') return;
    down = true;
    dragged = false;
    startX = e.clientX;
    startLeft = rail.scrollLeft;
  });
  rail.addEventListener('pointermove', (e) => {
    if (!down) return;
    const dx = e.clientX - startX;
    if (Math.abs(dx) > 4) {
      dragged = true;
      rail.classList.add('is-dragging');
      rail.setPointerCapture(e.pointerId);
    }
    if (dragged) rail.scrollLeft = startLeft - dx;
  });
  const end = () => {
    down = false;
    rail.classList.remove('is-dragging');
  };
  rail.addEventListener('pointerup', end);
  rail.addEventListener('pointercancel', end);
  // Kill the click only when the pointer actually dragged.
  rail.addEventListener('click', (e) => {
    if (dragged) {
      e.preventDefault();
      dragged = false;
    }
  }, true);
}

// Masked word-by-word title reveal: split each chapter title into
// overflow-hidden word masks; CSS slides the words up with a stagger when
// the section's [data-anim] ancestor gains .is-revealed. Skipped for
// reduced motion (and no-JS), where the plain title just shows.
function initTitleReveals() {
  if (REDUCED) return;
  qsa('.ap-av2-chapter__title').forEach((title) => {
    const frag = document.createDocumentFragment();
    let wi = 0;
    const addWord = (content, isNode) => {
      const mask = document.createElement('span');
      mask.className = 'ap-av2-w';
      const inner = document.createElement('span');
      inner.className = 'ap-av2-w__i';
      inner.style.setProperty('--wi', wi++);
      if (isNode) inner.appendChild(content);
      else inner.textContent = content;
      mask.appendChild(inner);
      frag.appendChild(mask);
    };
    Array.from(title.childNodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) frag.appendChild(document.createTextNode(' '));
          else addWord(part, false);
        });
      } else {
        // Accent span: keep it whole so its underline spans the phrase.
        addWord(node.cloneNode(true), true);
      }
    });
    title.textContent = '';
    title.appendChild(frag);
  });
}

// Magnetic CTA buttons: the button leans toward the cursor while hovered.
function initMagnetic() {
  if (!FINE_POINTER || REDUCED) return;
  qsa('.ap-av2-hero__cta .ap-btn, .ap-av2-cta__actions .ap-btn').forEach((btn) => {
    btn.classList.add('ap-av2-magnet');
    btn.addEventListener('pointermove', (e) => {
      const r = btn.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      btn.style.transform = `translate(${dx * 0.16}px, ${dy * 0.22}px)`;
    });
    btn.addEventListener('pointerleave', () => { btn.style.transform = ''; });
  });
}

// The journey drives itself: while the dark band scrolls through the
// viewport, the timeline pans 2010 → AZI. Hands off as soon as the user
// grabs it (drag / wheel / touch) so it never fights real input.
function initTimelineDrift() {
  const rail = qs('[data-av2-timeline]');
  if (!rail || REDUCED) return;
  // On touch devices the timeline is explored by native horizontal SWIPE, so the
  // scroll-driven auto-pan is skipped: otherwise, while the section sits on screen
  // the pan parks it on the 2nd milestone (progress ≈ 0.5 when centred), which
  // reads as "the timeline starts at step 2" on mobile. Desktop keeps the pan.
  if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) return;
  let userTook = false;
  ['pointerdown', 'wheel', 'touchstart'].forEach((t) =>
    rail.addEventListener(t, () => { userTook = true; }, { passive: true }));
  let ticking = false;
  const update = () => {
    ticking = false;
    if (userTook) return;
    const rect = rail.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.bottom < 0 || rect.top > vh) return;
    const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)));
    const max = rail.scrollWidth - rail.clientWidth;
    if (max > 0) rail.scrollLeft = progress * max * 0.55;
  };
  window.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }, { passive: true });
  update();
}

export function initAboutPage() {
  initSpotlight();
  initParallax();
  initTimelineDrag();
  initTiltMarquee();
  initTitleReveals();
  initMagnetic();
  initTimelineDrift();
}
